// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {type SyncedPreferences, SyncedPreferencesSchema} from '@app/vendor/schema/domains/user/SyncedPreferencesCodec';
import {fromBinary, toBinary} from '@bufbuild/protobuf';
import initLibfluxcore, * as wasm from '@pkgs/libfluxcore/libfluxcore';

const logger = new Logger('SyncedPreferencesEngine');
const EMPTY_BYTES = new Uint8Array();
let modulePromise: Promise<void> | null = null;

export type SyncedPreferencesField = Exclude<keyof SyncedPreferences, '$typeName' | '$unknown'>;

export type {SyncedPreferences};
export type SyncedPreferencesSubField = Exclude<SyncedPreferencesField, 'sanitizeUrls'>;

interface DecodeResult {
	ok: boolean;
	bytes: ArrayLike<number> | Uint8Array;
	error?: string | null;
}

interface MergeIncomingResult {
	merged: ArrayLike<number> | Uint8Array;
	wire: ArrayLike<number> | Uint8Array;
	dirty_fields?: ArrayLike<number> | Uint32Array;
	dirtyFields?: ArrayLike<number> | Uint32Array;
}

async function loadModule(): Promise<void> {
	if (modulePromise == null) {
		modulePromise = (async () => {
			try {
				if (typeof initLibfluxcore === 'function') {
					await initLibfluxcore();
				}
			} catch (error) {
				modulePromise = null;
				logger.warn('Failed to load synced preferences wasm engine', error);
				throw error;
			}
		})();
	}
	await modulePromise;
}

export async function ensureSyncedPreferencesEngineReady(): Promise<void> {
	await loadModule();
}

void loadModule();

export const SYNCED_PREFERENCES_FIELDS = SyncedPreferencesSchema.fields.map(
	(field) => field.localName as SyncedPreferencesField,
);

export const SYNCED_PREFERENCES_FIELD_NAMES = new Set<string>(SYNCED_PREFERENCES_FIELDS.map(String));

const FIELD_TO_NUMBER = new Map<SyncedPreferencesField, number>(
	SyncedPreferencesSchema.fields.map((field) => [field.localName as SyncedPreferencesField, field.number]),
);

const NUMBER_TO_FIELD = new Map<number, SyncedPreferencesField>(
	SyncedPreferencesSchema.fields.map((field) => [field.number, field.localName as SyncedPreferencesField]),
);

export function isSyncedPreferencesField(value: unknown): value is SyncedPreferencesField {
	return typeof value === 'string' && SYNCED_PREFERENCES_FIELD_NAMES.has(value);
}

function fieldNumber(field: SyncedPreferencesField): number {
	const number = FIELD_TO_NUMBER.get(field);
	if (number == null) {
		throw new Error(`Unknown synced preferences field: ${String(field)}`);
	}
	return number;
}

function fieldName(number: number): SyncedPreferencesField | null {
	return NUMBER_TO_FIELD.get(number) ?? null;
}

function toUint8Array(value: ArrayLike<number> | Uint8Array): Uint8Array {
	return value instanceof Uint8Array ? value : Uint8Array.from(value);
}

function toFieldNumbers(fields: Iterable<SyncedPreferencesField>): Uint32Array {
	return Uint32Array.from(Array.from(fields, fieldNumber));
}

function toFieldNames(fieldNumbers: ArrayLike<number>): Array<SyncedPreferencesField> {
	const fields: Array<SyncedPreferencesField> = [];
	for (let index = 0; index < fieldNumbers.length; index += 1) {
		const number = fieldNumbers[index];
		const field = fieldName(number);
		if (field != null) fields.push(field);
	}
	return fields;
}

export function preferencesToBytes(preferences: SyncedPreferences): Uint8Array {
	return toBinary(SyncedPreferencesSchema, preferences);
}

export function preferencesFromBytes(bytes: Uint8Array): SyncedPreferences {
	if (bytes.byteLength === 0) return createEmptySyncedPreferences();
	return fromBinary(SyncedPreferencesSchema, bytes);
}

export function createEmptySyncedPreferences(): SyncedPreferences {
	return fromBinary(SyncedPreferencesSchema, EMPTY_BYTES);
}

export function encodeSyncedPreferences(preferences: SyncedPreferences): string {
	return wasm.synced_preferences_encode_base64(preferencesToBytes(preferences));
}

export function decodeSyncedPreferencesBytes(encoded: string | null | undefined): Uint8Array {
	const result = wasm.synced_preferences_decode_base64(encoded ?? null) as DecodeResult;
	if (!result.ok) {
		throw new SyncedPreferencesDecodeError(result.error ?? 'invalid synced preferences');
	}
	return toUint8Array(result.bytes);
}

export function decodeSyncedPreferences(encoded: string | null | undefined): SyncedPreferences {
	try {
		return preferencesFromBytes(decodeSyncedPreferencesBytes(encoded));
	} catch (error) {
		if (error instanceof SyncedPreferencesDecodeError) throw error;
		throw new SyncedPreferencesDecodeError(error instanceof Error ? error.message : 'invalid synced preferences');
	}
}

export function decodeSyncedPreferencesLenient(encoded: string | null | undefined): SyncedPreferences {
	try {
		return preferencesFromBytes(toUint8Array(wasm.synced_preferences_decode_base64_lenient(encoded ?? null)));
	} catch {
		return createEmptySyncedPreferences();
	}
}

export function changedSyncedPreferenceFields(
	left: SyncedPreferences,
	right: SyncedPreferences,
): Array<SyncedPreferencesField> {
	return toFieldNames(wasm.synced_preferences_changed_fields(preferencesToBytes(left), preferencesToBytes(right)));
}

export function copySyncedPreferenceField(
	target: SyncedPreferences,
	source: SyncedPreferences,
	field: SyncedPreferencesField,
): SyncedPreferences {
	return preferencesFromBytes(
		wasm.synced_preferences_copy_field(preferencesToBytes(target), preferencesToBytes(source), fieldNumber(field)),
	);
}

export function mergeIncomingSyncedPreferences(args: {
	local: SyncedPreferences;
	wire: SyncedPreferences;
	incoming: SyncedPreferences;
	protectedFields: Iterable<SyncedPreferencesField>;
	recentlyAckedFields: Iterable<SyncedPreferencesField>;
	inFlight: SyncedPreferences | null;
	syncInFlight: boolean;
}): {
	merged: SyncedPreferences;
	wire: SyncedPreferences;
	dirtyFields: Array<SyncedPreferencesField>;
} {
	const result = wasm.synced_preferences_merge_incoming(
		preferencesToBytes(args.local),
		preferencesToBytes(args.wire),
		preferencesToBytes(args.incoming),
		toFieldNumbers(args.protectedFields),
		toFieldNumbers(args.recentlyAckedFields),
		args.inFlight == null ? null : preferencesToBytes(args.inFlight),
		args.syncInFlight,
	) as MergeIncomingResult;
	return {
		merged: preferencesFromBytes(toUint8Array(result.merged)),
		wire: preferencesFromBytes(toUint8Array(result.wire)),
		dirtyFields: toFieldNames(result.dirtyFields ?? result.dirty_fields ?? []),
	};
}

export function isEmptySyncedPreferencesEncoded(encoded: string | null | undefined): boolean {
	return !encoded;
}

export class SyncedPreferencesDecodeError extends Error {
	constructor(message: string) {
		super(`failed to decode synced_preferences: ${message}`);
		this.name = 'SyncedPreferencesDecodeError';
	}
}
